if (!customElements.get("product-modal-gallery")) {
  class ProductModalGallery extends HTMLElement {
    constructor() {
      super();
      this.swiper = undefined;
      this.content = this.querySelector("[data-modal-gallery-content]");
      this.contentLoaded = false;
      this.loadingPromise = null;
      this.isFullImage = false;
      this.listen();
      this._variantChangeHandler = (event) => {
        if (!this.content) return;
        this.content.dataset.lazyUrl = `/products/${event.detail.product}?variant=${event.detail.variant}&view=modal-gallery`;
        this.resetContent();
        if (this.style.display === "block") {
          this.loadContent().then(() => {
            this.swiper && this.swiper.update();
            this.playActiveVideo();
          });
        }
      };
      window.addEventListener("variantChange", this._variantChangeHandler);
    }

    resetContent() {
      this.setFullImage(false);
      this.pauseVideos();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = undefined;
      }
      this.contentLoaded = false;
      this.loadingPromise = null;
      if (this.content) {
        this.content.innerHTML =
          '<span class="loader loader--black loader--centered"></span>';
      }
    }

    loadContent() {
      if (this.contentLoaded) return Promise.resolve();
      if (this.loadingPromise) return this.loadingPromise;
      if (!this.content?.dataset.lazyUrl) return Promise.resolve();

      this.loadingPromise = fetch(this.content.dataset.lazyUrl)
        .then((response) => response.text())
        .then((html) => {
          this.content.innerHTML = html;
          this.contentLoaded = true;
          this.loadingPromise = null;
          this.initSwiper();
          setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
            this.swiper && this.swiper.update();
          }, 100);
        })
        .catch(() => {
          this.loadingPromise = null;
        });

      return this.loadingPromise;
    }

    initSwiper() {
      if (this.swiper || !this.querySelector(".swiper-container")) return;
      this.swiper = new Swiper(this.querySelector(".swiper-container"), {
        loop: false,
        grabCursor: false,
        speed: 500,
        spaceBetween: 8,
        slidesPerView: 1,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        on: {
          slideChangeTransitionEnd: () => {
            this.playActiveVideo();
          },
        },
      });
    }

    hydrateVideo(video, shouldPlay = false) {
      if (!video) return;
      if (video.dataset.videoLoaded === "true") {
        if (shouldPlay) {
          video.play().catch(() => {});
        }
        return;
      }
      video.querySelectorAll("source[data-src]").forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });
      video.dataset.videoLoaded = "true";
      video.load();
      if (shouldPlay) {
        video.play().catch(() => {});
      }
    }

    pauseVideos() {
      this.querySelectorAll("video").forEach((video) => {
        video.pause();
      });
    }

    playActiveVideo() {
      this.pauseVideos();
      const video = this.querySelector(".swiper-slide-active video");
      if (video) {
        this.hydrateVideo(video, true);
      }
    }

    setFullImage(active) {
      this.isFullImage = active;
      this.classList.toggle("product__modal-gallery--full-image", active);
      if (this.swiper) {
        this.swiper.allowTouchMove = !active;
        this.swiper.allowSlideNext = !active;
        this.swiper.allowSlidePrev = !active;
        this.swiper.update();
      }
    }

    onFullImageClick(event) {
      const trigger = event.target.closest("[data-modal-gallery-full-image]");
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();
      this.setFullImage(!this.isFullImage);
    }

    listen() {
      if (this._docClickHandler) {
        document.removeEventListener("click", this._docClickHandler);
      }

      this.querySelector(".product__modal-gallery__close")?.addEventListener(
        "click",
        () => {
          this.close();
        },
      );

      this._fullImageClickHandler = (event) => this.onFullImageClick(event);
      this.addEventListener("click", this._fullImageClickHandler);

      this._docClickHandler = (event) => {
        const slide = event.target.closest(".product__gallery .swiper-slide");
        if (!slide) return;
        this.open(slide.dataset.index);
      };
      document.addEventListener("click", this._docClickHandler);
    }

    open(index = 0) {
      this.setFullImage(false);
      this.style.display = "block";
      document.body.style.overflow = "hidden";
      this.loadContent().then(() => {
        if (this.swiper) {
          this.swiper.slideTo(index, 0);
          this.swiper.update();
        }
        this.playActiveVideo();
      });
    }

    close() {
      this.pauseVideos();
      this.setFullImage(false);
      this.style.display = "none";
      document.body.style.overflow = "";
    }

    disconnectedCallback() {
      if (this._variantChangeHandler) {
        window.removeEventListener("variantChange", this._variantChangeHandler);
      }
      if (this._docClickHandler) {
        document.removeEventListener("click", this._docClickHandler);
      }
      if (this._fullImageClickHandler) {
        this.removeEventListener("click", this._fullImageClickHandler);
      }
    }
  }
  customElements.define("product-modal-gallery", ProductModalGallery);
}
